import { Fragment, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { RefreshCcw, Search, MessageSquarePlus, ChevronDown, ChevronRight, Building2 } from "lucide-react";
import api, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const inr = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const dmy = (iso) => { if (!iso) return "—"; const [y, m, d] = iso.split("-"); return `${d}/${m}/${y.slice(2)}`; };
const stamp = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "never");
const METHOD = { razorpay_link: "Razorpay link", bank: "Bank", cash: "Cash", upi: "UPI", card: "Card", other: "Other" };
const STATUS = {
  paid: { label: "Paid", cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30" },
  partial: { label: "Partial", cls: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30" },
  pending: { label: "Pending", cls: "bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30" },
};

function NotesRow({ row, onSaved }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post(`/studio-payments/${row.id}/notes`, { note }); setNote(""); onSaved(data); toast.success("Note added"); }
    catch (x) { toast.error(formatApiError(x?.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <tr className="bg-white/[0.02]"><td colSpan={8} className="px-3 py-3">
      <div className="space-y-1.5 mb-2" data-testid={`studio-notes-list-${row.id}`}>
        {row.fin_notes.length === 0 && <div className="text-xs text-white/40 italic">No Finance notes yet.</div>}
        {row.fin_notes.map((n, i) => (
          <div key={i} className="text-xs"><span className="text-white/80">{n.note}</span> <span className="text-white/40">— {n.by_name} · {stamp(n.at)}</span></div>
        ))}
      </div>
      <form onSubmit={save} className="flex gap-2">
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a Finance note (stays in LIM Flow only)…" data-testid={`studio-note-input-${row.id}`} className="h-8 text-sm" />
        <Button type="submit" disabled={busy || note.trim().length < 2} data-testid={`studio-note-save-${row.id}`} className="h-8 bg-[#E11D2E] hover:bg-[#F03041]">Add</Button>
      </form>
    </td></tr>
  );
}

export function StudioPayments() {
  const [data, setData] = useState({ items: [], summary: {}, last_sync: {} });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [status, setStatus] = useState("");
  const [month, setMonth] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);

  const load = async () => {
    try { const { data: d } = await api.get("/studio-payments"); setData(d); }
    catch (x) { toast.error(formatApiError(x?.response?.data?.detail) || "Failed to load studio payments"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const syncNow = async () => {
    setSyncing(true);
    try { const { data: s } = await api.post("/studio-payments/sync"); toast.success(`Synced ${s.count} bookings from LIME Studios`); await load(); }
    catch (x) { toast.error(formatApiError(x?.response?.data?.detail) || "Sync failed"); }
    finally { setSyncing(false); }
  };

  const rows = useMemo(() => data.items.filter((r) =>
    (!status || r.payment_status === status || (status === "pending" && r.payment_status === "partial")) && (!month || (r.date || "").startsWith(month)) &&
    (!q || `${r.name} ${r.package_name}`.toLowerCase().includes(q.toLowerCase()))), [data.items, status, month, q]);
  const sum = useMemo(() => rows.reduce((a, r) => ({ total: a.total + r.amount, paid: a.paid + r.amount_paid, pending: a.pending + r.amount_pending }), { total: 0, paid: 0, pending: 0 }), [rows]);
  const replace = (d) => setData((prev) => ({ ...prev, items: prev.items.map((x) => (x.id === d.id ? d : x)) }));
  const ls = data.last_sync || {};

  return (
    <div className="space-y-5" data-testid="studio-payments">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-display tracking-tight flex items-center gap-2"><Building2 size={18} className="text-sky-500" /> LIME Studios payments</h2>
          <p className="text-xs text-white/50 mt-1" data-testid="studio-last-sync">
            Pulled from the LIME Studios admin panel every 15 min · last synced {stamp(ls.at)}
            {ls.ok === false && <span className="text-red-500"> · last sync failed</span>}
          </p>
        </div>
        <Button variant="outline" onClick={syncNow} disabled={syncing} data-testid="studio-sync-btn" className="border-white/15">
          <RefreshCcw size={14} className={`mr-1.5 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing…" : "Sync now"}
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[["Total billed", sum.total, `${rows.length} booking${rows.length === 1 ? "" : "s"}`, "studio-sum-total", ""],
          ["Received", sum.paid, "paid so far", "studio-sum-paid", "text-emerald-600 dark:text-emerald-400"],
          ["Pending", sum.pending, "still to collect", "studio-sum-pending", "text-red-600 dark:text-red-400"]].map(([l, v, s, t, c]) => (
          <div key={t} className="rounded-lg border border-white/10 bg-white/[0.03] p-4" data-testid={t}>
            <div className="text-[0.7rem] uppercase tracking-widest text-white/50">{l}</div>
            <div className={`text-2xl font-display mt-1 ${c}`}>{inr(v)}</div>
            <div className="text-xs text-white/40">{s}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-64">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/40" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search client or package…" data-testid="studio-search" className="pl-8 h-9" />
        </div>
        {[["", "All"], ["paid", "Paid"], ["pending", "Pending (incl. partial)"], ["partial", "Partial"]].map(([k, l]) => (
          <button key={k || "all"} type="button" onClick={() => setStatus(k)} data-testid={`studio-filter-${k || "all"}`}
            className={`h-8 px-3 rounded-full border text-xs transition-colors ${status === k ? "border-[#E11D2E] bg-[#E11D2E]/10 text-[#E11D2E] font-semibold" : "border-white/15 hover:bg-white/5"}`}>{l}</button>
        ))}
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="studio-month" className="h-8 rounded-md border border-white/15 bg-transparent px-2 text-xs" />
        {month && <button type="button" onClick={() => setMonth("")} data-testid="studio-month-clear" className="text-xs text-white/50 hover:text-white">Clear month</button>}
      </div>

      <div className="rounded-lg border border-white/10 overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]">
          <thead className="text-[0.7rem] uppercase tracking-widest text-white/50 bg-white/[0.03]">
            <tr>{["Date", "Client", "Package", "Amount", "Paid", "Pending", "Status", "Notes"].map((h) => <th key={h} className="text-left font-medium px-3 py-2">{h}</th>)}</tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="px-3 py-6 text-center text-white/40">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={8} className="px-3 py-6 text-center text-white/40" data-testid="studio-empty">No studio payments match.</td></tr>}
            {rows.map((r) => {
              const st = STATUS[r.payment_status] || STATUS.pending;
              const isOpen = open === r.id;
              return (
                <Fragment key={r.id}>
                  <tr className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`studio-row-${r.id}`}>
                    <td className="px-3 py-2 whitespace-nowrap font-mono text-xs">{dmy(r.date)}<div className="text-white/40">{r.start_time}{r.end_time ? `–${r.end_time}` : ""}</div></td>
                    <td className="px-3 py-2"><div className="client-line">{r.name}</div></td>
                    <td className="px-3 py-2 text-white/70">{r.package_name}<div className="text-xs text-white/40">{r.duration}</div></td>
                    <td className="px-3 py-2 font-mono whitespace-nowrap">{inr(r.amount)}</td>
                    <td className="px-3 py-2 font-mono whitespace-nowrap text-emerald-600 dark:text-emerald-400">{inr(r.amount_paid)}</td>
                    <td className="px-3 py-2 font-mono whitespace-nowrap text-red-600 dark:text-red-400">{r.amount_pending ? inr(r.amount_pending) : "—"}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={`text-[11px] px-2 py-0.5 rounded-full border ${st.cls}`} data-testid={`studio-status-${r.id}`}>{st.label}</span>
                      <div className="text-[11px] text-white/40 mt-0.5">{METHOD[r.payment_method] || r.payment_method || ""}</div>
                    </td>
                    <td className="px-3 py-2">
                      <button type="button" onClick={() => setOpen(isOpen ? null : r.id)} data-testid={`studio-notes-toggle-${r.id}`}
                        className="inline-flex items-center gap-1 text-xs text-white/60 hover:text-white">
                        {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}<MessageSquarePlus size={12} /> {r.fin_notes.length || "Add"}
                      </button>
                    </td>
                  </tr>
                  {isOpen && <NotesRow row={r} onSaved={replace} />}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
